import React, { Fragment, useRef, useState } from 'react';
import styled from '@emotion/styled';
import Box from '@ff/ui/Box';
import Image from '@ff/ui/Image';
import Link from '@ff/ui/Link';
import Text from '@ff/ui/Text';
import MasonryLayout from 'shared/components/SocialMediaMessages/MasonryLayout';
import type { LayoutProps, SocialFeedEvents, isRtlLanguage } from './';
import type { SlideProps } from './highlightsDetailProps';
import { getHandlerByKey } from 'shared/lib/appHandlers';
import { IS_APP } from 'shared/lib/pathUtils';

export default function Highlight(props: SlideProps & SocialFeedEvents & isRtlLanguage) {
  const { isRtlLanguage, layout, slideNumber, asset, text, link, button, socialFeed, onScrollable } = props;
  const [fullScreenOnPlay, setFullScreenOnPlay] = useState(false);
  const videoRef = useRef(null);

  const isTextSlideVisible = (text && text.length > 0) || link || button;
  const assetCount = asset ? asset.length : 0;
  const hasSocialFeed = socialFeed && socialFeed?.messages?.length > 0;
  const breakpoints = {
    default: 2,
  };

  const toggleFullScreen = () => {
    // Only allow the video to go full screen once
    if (fullScreenOnPlay) return;

    if (videoRef.current?.requestFullscreen) {
      videoRef.current.requestFullscreen();
      setFullScreenOnPlay(true);
    }
  };

  // If app page and handler is provided, use the handler, else use the href
  return (
    <SlideContainer layout={layout}>
      {/* Text */}
      {isTextSlideVisible && (
        <SlideText layout={layout}>
          <InnerSlideText>
            {slideNumber?.number && (
              <Box display='flex' mb='23px'>
                <SlideNumber>
                  {slideNumber?.number}
                </SlideNumber>
                <Line isRtlLanguage={isRtlLanguage} />
                <SlideNumberContent>
                  {slideNumber?.content}
                </SlideNumberContent>
              </Box>
            )}
            {text?.map((s) => (
              <Fragment key={s.content}>
                {s.type === 'title' && s.content.length > 1 && <Title>{s?.content}</Title>}
                {s.type === 'subtitle' && s.content.length > 1 && <Subtitle>{s?.content}</Subtitle>}
                {s.type === 'paragraph' && s.content.length > 1 && <Description>{s?.content}</Description>}
              </Fragment>
            ))}
            {link?.length > 0 && link.map((l, index) => (
              <LinkContainer key={index}>
                <Link
                  href={!IS_APP || !getHandlerByKey(l.appHandlerKey) ? l?.src : undefined}
                  onClick={IS_APP ? getHandlerByKey(l.appHandlerKey) : undefined}
                  textDecoration='underline'
                >
                  {l?.content} {'>'}
                </Link>
              </LinkContainer>
            ))}
            {button?.length > 0 && button.map((b) => (
              <ButtonLink
                key={b.src}
                href={!IS_APP || !getHandlerByKey(b.appHandlerKey) ? b?.src : undefined}
                onClick={IS_APP ? getHandlerByKey(b.appHandlerKey) : undefined}
              >
                {b?.content}
              </ButtonLink>
            ))}
          </InnerSlideText>
        </SlideText>
      )}

      {/* Asset */}
      <SlideAsset length={assetCount} layout={layout}>
        {asset?.map((element, index, array) => {
          const isVideo = /(mp4|mov)$/.test(element.src);
          return (
            <AssetContainer key={element.src} extendWidth={element?.extendWidth} layout={layout}>
              {isVideo ? (
                <Video
                  ref={videoRef}
                  controls
                  controlsList='nodownload noremoteplayback'
                  onPlay={toggleFullScreen}
                >
                  <source src={element.src} />
                </Video>
              ) : (
                element.src && (
                  <SlideImage
                    length={array.length}
                    src={element.src}
                    alt={element?.imageAlt || ''}
                  />
                )
              )}
            </AssetContainer>
          )
        })}

        {/* Social Media Feed */}
        {hasSocialFeed && (
          <SocialFeedContainer layout={layout}>
            <Box
              onMouseEnter={() => onScrollable(true)}
              onMouseLeave={() => onScrollable(false)}
            >
              <MasonryLayout
                breakpoints={breakpoints}
                messages={...socialFeed.messages}
              />
            </Box>
          </SocialFeedContainer>
        )}
      </SlideAsset>
    </SlideContainer>
  );
}

const SlideContainer = styled(Box)<{ layout: 'TextFirst' | 'ImageFirst' }>(({ theme, layout }) => ({
  alignItems: layout === 'TextFirst' ? 'start' : 'end',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: 0,
  width: '100%',
  [theme.mediaQueries.md]: {
    alignItems: 'center',
    flexDirection: layout === 'TextFirst' ? 'row' : 'row-reverse',
  }
}));

const SlideText = styled(Box)<{ layout: LayoutProps }>(({ theme, layout }) => ({
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
  marginBottom: '40px',
  marginTop: '20px',
  maxWidth: '100%',
  minWidth: '100%',
  flexDirection: 'column',
  paddingLeft: 0,
  width: '100%',
  [theme.mediaQueries.md]: {
    alignItems: layout === 'TextFirst' ? 'start' : 'end',
    marginBottom: 0,
    marginTop: 0,
    maxWidth: layout === 'TextFirst' ? '75%' : 'auto',
    minWidth: '384px',
    paddingLeft: '10vw',
    paddingRight: '10vw',
    width: 'auto',
  },
  [theme.mediaQueries.lg]: {
    width: '100%',
  }
}));

const InnerSlideText = styled(Box)(({ theme }) => ({
  [theme.mediaQueries.lg]: {
    maxWidth: '436px',
  },
}));

const SlideNumber = styled(Text)({
  color: '#CF2B2B',
  fontSize: '32px',
  fontWeight: 300,
  lineHeight: '26px',
});

const Line = styled(Box)<{ isRtlLanguage: boolean }>(({ isRtlLanguage }) => ({
  ':after': {
    content: "''",
    borderRight: '1px solid #CF2B2B',
    display: 'inline-block',
    height: '45px',
    marginLeft: '3px',
    marginTop: '3px',
    transform: isRtlLanguage ? 'rotate(330deg)' : 'rotate(30deg)',
  }
}));

const SlideNumberContent = styled(Box)({
  alignItems: 'end',
  color: '#DC8A8A',
  display: 'flex',
  fontSize: '11px',
  paddingBottom: '7px',
  paddingLeft: '6px',
  textTransform: 'uppercase',
});

const LinkContainer = styled(Box)(({ theme }) => ({
  marginTop: '20px',
  [theme.mediaQueries.md]: {
    marginTop: '48px',
  }
}));

const ButtonLink = styled(Link)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: 'rgba(23, 26, 32, 1)',
  border: '1px solid rgba(23, 26, 32, 0.65)',
  fontWeight: theme.fontWeights.light,
  fontSize: '14px',
  lineHeight: '26px',
  marginTop: '30px',
  padding: '8px 27px'
}));

const SlideAsset = styled(Box)<{ length: number, layout: LayoutProps }>(({ theme, length, layout }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '10px',
  width: '100%',
  [theme.mediaQueries.md]: {
    alignItems: layout === 'TextFirst' ? 'end' : 'start',
    display: 'flex',
    flexDirection: length === 2 ? 'row' : 'column',
    minWidth: '50vw',
    paddingLeft: length > 1 ?
      layout === 'TextFirst' ? 0 : '10vw'
      : 0,
    paddingRight: length > 1 ?
      layout === 'TextFirst' ? '10vw' : 0
      : 0,
    width: 'auto',
  }
}));

const Video = styled(Box)<JSX.IntrinsicElements['video']>(({ theme }) => ({
  height: 'auto',
  maxWidth: '100%',
  objectFit: 'cover',
  objectPosition: 'center',
  width: '100%',
  [theme.mediaQueries.md]: {
    height: '100vh',
  }
}));
Video.defaultProps = { as: 'video' };

const SocialFeedContainer = styled(Box)<{ layout: LayoutProps }>(({ theme, layout }) => ({
  paddingBottom: '6vw',
  width: 'auto',
  [theme.mediaQueries.md]: {
    height: '100vh',
    maxWidth: '800px',
    overflowY: 'scroll',
    paddingLeft: layout === 'TextFirst' ? 0 : '9vw',
    paddingRight: layout === 'TextFirst' ? '9vw' : 0,
  },
  [theme.mediaQueries.xl]: {
    maxWidth: '100%',
  }
}));

const AssetContainer = styled(Box)<{ extendWidth: boolean, layout: LayoutProps }>(({ theme, extendWidth, layout }) => ({
  width: 'auto',
  [theme.mediaQueries.md]: {
    width: extendWidth ? '100vw' : 'auto',
    transform: extendWidth ? layout === 'TextFirst' ?
      'translateX(50vw)' : 'translateX(-50vw)'
      : 'none',
  },
}));

const SlideImage = styled(Image)<{ length: number | undefined }>(({ theme, length }) => ({
  alignSelf: 'flex-start',
  display: 'flex',
  flex: '0 0 auto',
  height: '100%',
  objectFit: 'cover',
  width: '100%',
  [theme.mediaQueries.md]: {
    height: (length && length === 1) ? '100vh' : 'auto',
    minHeight: 'auto',
    /**
     * Handle 3 or more images:
     * Max Height:
     *  - Give each image the same height
     *  - Add padding to the top and bottom of the column (-em)
     *
     * Width:
     * - Set each image to take a width 30vw of the column
     */
    maxHeight: (length && length >= 3) ? `calc((100vh / ${length}) - 3em)` : 'auto',
    width: (length && length >= 3) ? '30vw' : 'auto',
  }
}));

const Title = styled(Text)(({ theme }) => ({
  fontSize: 30,
  fontWeight: 400,
  color: '#171A20',
  lineHeight: '36px',
  marginBottom: '25px',
  [theme.mediaQueries.md]: {
    marginBottom: '38px',
  }
}));

const Subtitle = styled(Text)(({ theme }) => ({
  fontSize: 17,
  lineHeight: '26px',
  color: '#171A20',
  fontWeight: theme.fontWeights.regular,
  marginBottom: '4px',
  marginTop: '16px',
}));

const Description = styled(Text)(({ theme }) => ({
  fontSize: 14,
  fontWeight: theme.fontWeights.light,
  color: '#171A20',
  lineHeight: '22px',
  marginTop: '16px',
}));
